import { ReportTemplate } from "src/types";
import { v4 as uuidv4 } from "uuid";
import Input from "../Inputs/Input";
import { useUser } from "src/hooks/useUser";
import { Tooltip } from "flowbite-react";
import { Link } from "react-router-dom";

interface Props {
  template: ReportTemplate;
  setTemplate: (template: ReportTemplate) => void;
  onClose?: () => void;
}

const SpecialInputLabel = ({
  onClose,
  featureName,
  children,
  isOn,
}: {
  onClose?: () => void;
  featureName: string;
  children: React.ReactNode;
  isOn?: boolean;
}) => {
  if (isOn) return <>{children}</>;
  return (
    <Tooltip
      content={
        <div>
          This page is only available when {featureName} feature is enabled.
          <Link
            to="/profile#extra-features"
            onClick={() => {
              onClose?.();
            }}
          >
            <div className="underline">Enable it in Settings</div>
          </Link>
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};

const TemplateOptions = ({ template, setTemplate, onClose }: Props) => {
  const { user } = useUser();
  const exists = (name: string) =>
    template.settings.some((page) => page.name === name);
  const newPage = (name: string) => ({
    id: uuidv4(),
    name: name as any,
    settings: {},
  });

  const flipPage = (page: any) => (exists: boolean) => {
    console.log("click", exists);
    if (!exists) {
      console.log("removing page", page);
      // Remove the page from the settings

      setTemplate({
        ...template,
        settings: template.settings.filter((p) => p.name !== page),
      });
    } else {
      setTemplate({
        ...template,
        settings: [...template.settings, newPage(page)],
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="text-2xl font-semibold">Included Pages</div>
      <div>
        <div className="font-medium text-xl text-left">Incomes</div>
        <div className="grid grid-cols-1">
          <Input
            subtype="toggle"
            labelLength={120}
            label="Income Table"
            value={exists("incomes")}
            setValue={flipPage("incomes")}
          />
          <Input
            subtype="toggle"
            labelLength={120}
            label="Income Chart"
            value={exists("income-chart")}
            setValue={flipPage("income-chart")}
          />
          <Input
            subtype="toggle"
            labelLength={120}
            label="By Person"
            value={exists("by-person")}
            setValue={flipPage("by-person")}
          />
          <SpecialInputLabel
            isOn={user?.info?.taxesFlag}
            onClose={onClose}
            featureName="Tax"
          >
            <Input
              subtype="toggle"
              disabled={!user?.info?.taxesFlag}
              labelLength={120}
              label={
                <span
                  className={`${!user?.info?.taxesFlag && "text-gray-400"}`}
                >
                  By Tax Status
                </span>
              }
              value={exists("by-tax-status")}
              setValue={flipPage("by-tax-status")}
            />
          </SpecialInputLabel>
          <Input
            subtype="toggle"
            labelLength={120}
            label="By Income Type"
            value={exists("by-income-type")}
            setValue={flipPage("by-income-type")}
          />
        </div>
      </div>
      <div>
        <div className="font-medium text-xl text-left">Spending</div>
        <div className="grid grid-cols-1 gap-x-5">
          <SpecialInputLabel
            isOn={user?.info?.needsFlag}
            onClose={onClose}
            featureName="Spending"
          >
            <Input
              subtype="toggle"
              labelLength={120}
              disabled={!user?.info?.needsFlag}
              label={
                <span
                  className={`${!user?.info?.needsFlag && "text-gray-400"}`}
                >
                  Spending Table
                </span>
              }
              value={exists("spending")}
              setValue={flipPage("spending")}
            />
          </SpecialInputLabel>
          <SpecialInputLabel
            isOn={user?.info?.needsFlag}
            onClose={onClose}
            featureName="Spending"
          >
            <Input
              subtype="toggle"
              disabled={!user?.info?.needsFlag}
              label={
                <span
                  className={`${!user?.info?.needsFlag && "text-gray-400"}`}
                >
                  Spending Chart
                </span>
              }
              labelLength={120}
              value={exists("spending-chart")}
              setValue={flipPage("spending-chart")}
            />
          </SpecialInputLabel>
        </div>
      </div>

      <div>
        <div className="font-medium text-xl text-left">Others</div>
        <div className="grid grid-cols-1 gap-x-5">
          <Input
            subtype="toggle"
            label="Cover"
            labelLength={120}
            value={exists("cover")}
            setValue={flipPage("cover")}
          />

          <Input
            subtype="toggle"
            label="Composite"
            labelLength={120}
            value={exists("composite")}
            setValue={flipPage("composite")}
          />
          <SpecialInputLabel
            isOn={user?.info?.longevityFlag}
            onClose={onClose}
            featureName="Longevity"
          >
            <Input
              subtype="toggle"
              labelLength={120}
              disabled={!user?.info?.longevityFlag}
              label={
                <span
                  className={`${!user?.info?.longevityFlag && "text-gray-400"}`}
                >
                  Longevity
                </span>
              }
              value={exists("longevity")}
              setValue={flipPage("longevity")}
            />
          </SpecialInputLabel>
        </div>
      </div>
    </div>
  );
};

export default TemplateOptions;
